import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
    Slider,
    Button,
    Input,
    Select,
    AutoComplete,
    Checkbox,
    DatePicker,
    notification,
    Modal,
} from "antd";
import close from '../../images/close.svg';
import Loader from "./components/loader";
import AutoSearchBox from "./components/autoSearchBox";
import AutoSearchInput from "./components/autoSearchInput";
import { functionService } from "../../lib/functions";
import * as Styled from "./styled";
import Styles from "./styles/demandForm.module.scss";
import { useSelector } from "react-redux";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import CoffeeeButton from "../../components/uiKit/button";
import { Dropdown } from "react-bootstrap";
import Sidemenu from "../../components/sidemenu";
import { useNavigate } from "react-router-dom";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

/* ─── Design tokens ─────────────────────────────────────────────────────── */
const T = {
    pink: "#FF2C91",
    blue: "#1B49EA",
    navy: "#0d2172",
    border: "#c9c9c9",
    bg: "#f7f8fc",
    white: "#fff",
    text: "#1E2228",
    muted: "#939599",
    red: "#f04638",
    radius: 10,
    inputH: 54,
};

const S = {
    col3: { flex: "1 1 calc(33.33% - 14px)", minWidth: 180, marginBottom: 20 },
    label: { fontWeight: 600, fontSize: 14, marginBottom: 6, display: "block", color: T.text },
    border: `1px solid ${T.border}`,
};

var workExpYear = [];
for (var i = 0; i <= 25; i++) { workExpYear.push({ key: i, value: i }); }

var workExpMonth = [];
for (var i = 0; i < 12; i++) { workExpMonth.push({ key: i, value: i }); }

var jobTypeOptions = [
    { key: "fulltime", value: "Full-time" },
    { key: "parttime", value: "Part-time" },
    { key: "contract", value: "Contract" },
    { key: "contract_to_hire", value: "Contract to Hire" },
];

var officeDaysOptions = [
    { key: 1, value: "1 Day" },
    { key: 2, value: "2 Days" },
    { key: 3, value: "3 Days" },
    { key: 4, value: "4 Days" },
];

var assessmentRequiredOptions = [
    { key: "yes", value: "YES" },
    { key: "no", value: "NO" },
];

var demandCategoryOptions = [
    { key: "High Probability Closure", value: "High Probability Closure" },
    { key: "Likely Closure", value: "Likely Closure" },
    { key: "Maybe a Closure", value: "Maybe a Closure" },
    { key: "New Requirement", value: "New Requirement" },
];

/* ─── Helper: is created_at date the same calendar day as today? ─────────── */
const isCreatedToday = (createdAt) => {
    if (!createdAt) return false;
    const created = new Date(createdAt);
    const today = new Date();
    return (
        created.getFullYear() === today.getFullYear() &&
        created.getMonth() === today.getMonth() &&
        created.getDate() === today.getDate()
    );
};

/* ─── Helper: resolve a skill value (UUID or name-string) against filterList ─
   Returns { id, name } always.
   - If val is already an object with id+name → return as-is
   - If val is a UUID present in filterList → return {id, name} from list
   - If val is a UUID NOT in filterList → return {id: val, name: val} (best effort)
   - If val is a plain name string → find by name in filterList, or treat as custom
────────────────────────────────────────────────────────────────────────────── */
const resolveSkillObject = (val, filterList) => {
    if (!val) return null;
    if (typeof val === "object" && val !== null && (val.id || val.name)) {
        return { id: val.id ?? val.name, name: val.name ?? val.id };
    }
    const strVal = String(val);
    const byId = filterList?.find(s => s.id === strVal);
    if (byId) return { id: byId.id, name: byId.name };
    const byName = filterList?.find(s => s.name?.toLowerCase() === strVal.toLowerCase());
    if (byName) return { id: byName.id, name: byName.name };
    return { id: strVal, name: strVal };
};


/* ─── SkillPair ──────────────────────────────────────────────────────────── */
const SkillPair = ({ skillKey, label, required, disabled, data, filterList, onSkillSearch, skillUpdate }) => {
    const isUUIDStr = (val) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(val || ""));

    const [displayText, setDisplayText] = React.useState("");
    // Track whether this field has been "resolved" from its initial UUID load
    const resolvedRef = React.useRef(false);
    // Track whether user currently has focus / is actively interacting
    const userActiveRef = React.useRef(false);

    // ONE-TIME resolution: runs when filterList loads OR when data[skillKey] changes
    useEffect(() => {
        if (!filterList || filterList.length === 0) return;
        if (userActiveRef.current) return;
        const val = data[skillKey];

        console.log(`SkillPair [${skillKey}] resolving:`, val, "filterList size:", filterList.length);

        if (!val) {
            setDisplayText("");
            resolvedRef.current = false;
            return;
        }

        const resolved = resolveSkillObject(val, filterList);

        console.log(`SkillPair [${skillKey}] resolved to:`, resolved);

        if (resolved?.name && !isUUIDStr(resolved.name)) {
            setDisplayText(resolved.name);
            resolvedRef.current = true;
        }
    }, [filterList, skillKey, data[skillKey]]);  // ← NOW watches data[skillKey] too

    // When the parent explicitly clears this skill slot (e.g. clearing skill3 should clear skill4+5)
    // We detect this by watching for val going null/undefined from outside
    const prevValRef = React.useRef(data[skillKey]);
    useEffect(() => {
        const val = data[skillKey];
        const prevVal = prevValRef.current;
        prevValRef.current = val;
        // If value was cleared from outside (not by user typing), reset display
        if (!val && prevVal && !userActiveRef.current) {
            setDisplayText("");
            resolvedRef.current = false;
        }
    }, [data[skillKey]]);

    const handleChange = (val) => {
        if (disabled) return;
        userActiveRef.current = true;
        resolvedRef.current = true; // user is now in control
        setDisplayText(val ?? "");
        if (!val || val.trim() === "") {
            skillUpdate(skillKey, null);
        } else {
            skillUpdate(skillKey, val);
        }
        onSkillSearch(val || "");
    };

    const handleSelect = (optionValue, option) => {
        if (disabled) return;
        userActiveRef.current = false; // selection done, no longer "typing"
        resolvedRef.current = true;
        setDisplayText(option.label);
        skillUpdate(skillKey, optionValue);
    };


    const handleClear = () => {
        if (disabled) return;
        userActiveRef.current = false;
        resolvedRef.current = false; // allow re-resolution if needed
        setDisplayText("");
        skillUpdate(skillKey, null);
    };

    const handleFocus = () => { userActiveRef.current = true; };
    const handleBlur = () => { userActiveRef.current = false; };

    return (
        <div style={{ width: "32%", opacity: disabled ? 0.4 : 1, transition: "opacity 0.2s" }}>
            <p style={{ fontWeight: "700", fontSize: "16px", width: "100%" }}>
                {label}{required && <span style={{ color: T.red }}>*</span>}
            </p>
            <div style={{ width: "100%" }}>
                <AutoComplete
                    style={{ width: "100%" }}
                    placeholder={disabled ? "Fill previous skill first" : "Select or type skill"}
                    value={displayText}
                    disabled={disabled}
                    options={filterList?.map(item => ({ value: item.id, label: item.name }))}
                    onChange={handleChange}
                    onSelect={handleSelect}
                    onClear={handleClear}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    dropdownStyle={{ zIndex: 9999 }}
                    allowClear
                />
            </div>
        </div>
    );
};

/* ─── Component ──────────────────────────────────────────────────────────── */
const DemandForm = ({ queryParams }) => {
    const [skillOptions, setSkillOptions] = useState([]);
    const [otherSkillOptions, setOtherSkillOptions] = useState([]);
    const [locationsOptions, setLocationOptions] = useState([]);
    const [roleTitleOptions, setRoleTitleOptions] = useState([]);
    const [recruiterOptions, setRecruiterOptions] = useState([]);
    const [TLOptions, setTLOptions] = useState([]);
    const [filterList, setFilter] = useState([]);

    const [JDData, setJDData] = useState("");
    const [JDName, setJDName] = useState("");
    const [miscellaneousFileName, setMiscellaneousFileName] = useState("");

    const user = useSelector((state) => state?.recruiter?.companyData?.companyDetails);

    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});

    const setFieldError = (field, msg) => setFieldErrors(prev => ({ ...prev, [field]: msg || undefined }));
    const clearFieldError = (field) => setFieldErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    const FieldError = ({ field }) => fieldErrors[field]
        ? <p style={{ color: "#f04638", fontSize: 12, marginTop: 4, marginBottom: 0, fontWeight: 500 }}>⚠ {fieldErrors[field]}</p>
        : null;

    const [showMore, setShowMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const { recruiter_id, search_id } = useParams();
    const [errorMessage, setErrorMessage] = useState(null);
    const [demandTableId, setDemandTableId] = useState(null);
    const [recruiterTableId, setRecruiterTableId] = useState(null);
    const [dataObj, setDataObj] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [createdAt, setCreatedAt] = useState(null);

    useEffect(() => {
        const dId = localStorage.getItem("demand_table_id");
        const rId = localStorage.getItem("demand_recruiter_id");
        if (dId && rId) { setDemandTableId(dId); setRecruiterTableId(rId); }
    }, []);

    /* ── CHANGE: fetchDemandDetails now lives here and depends on filterList ── */
    useEffect(() => {
        // Wait until skills are loaded AND we have the IDs
        if (filterList.length === 0) return;
        if (!demandTableId || !recruiterTableId) return;

        const fetchDemandDetails = async () => {
            console.log("🔥 fetchDemandDetails CALLED");
            try {
                const obj = { demand_id: demandTableId, recruiter_id: recruiterTableId };
                const res = await functionService.getDetailsFromDemandAndRecruiter(obj);

                const apiData = res.response.data.data[0];

                console.log(
                    "assignedRecruiterIds raw:",
                    typeof apiData.assignedRecruiterIds,
                    apiData.assignedRecruiterIds
                );

                console.log(
                    "recruiterNames raw:",
                    typeof apiData.recruiterNames,
                    apiData.recruiterNames
                );

                if (res?.status === true && res?.response?.data?.data) {
                    const apiData = res.response.data.data[0];

                    setCreatedAt(apiData.created_at || null);

                    /* ── CHANGE: resolve primary_skills (UUIDs or objects) → [{id,name}] ── */
                    const resolvedPrimarySkills = (apiData.primary_skills || []).map(skillVal =>
                        resolveSkillObject(skillVal, filterList)
                    ).filter(Boolean);

                    /* ── CHANGE: resolve other_skills the same way ── */
                    const resolvedOtherSkills = (apiData.other_skills || []).map(skillVal =>
                        resolveSkillObject(skillVal, filterList)
                    ).filter(Boolean);
                    console.log("apiData.ddr_job_type:", apiData.ddr_job_type)
                    const formattedData = {
                        company_name: apiData.company_name,
                        role_title: apiData.role_title,
                        job_code: apiData.job_code || "",
                        // job_type: (apiData.job_type || apiData.jobType || "").toLowerCase(),
                        job_type: (apiData.ddr_job_type || "").toLowerCase(),
                        locationType: apiData.recruiter_location_type || apiData.recruiterLocationType,
                        location: Array.isArray(apiData.recruiter_location)
                            ? apiData.recruiter_location
                            : typeof apiData.recruiter_location === "string"
                                ? apiData.recruiter_location.replace(/[{}"]/g, "").split(",").map(loc => loc.trim()).filter(Boolean)
                                : [],
                        min_yrs: apiData.min_yrs?.toString(),
                        max_yrs: apiData.max_yrs?.toString(),
                        minCtc: apiData.ddr_min_ctc?.toString(),
                        maxCtc: apiData.ddr_max_ctc?.toString(),
                        noticePeriod: apiData.ddr_notice_period?.toString(),
                        /* ── CHANGE: use resolved skill objects ── */
                        primarySkills: resolvedPrimarySkills,
                        otherSkills: resolvedOtherSkills,
                        assignedRecruiterIds: Array.isArray(apiData.assignedRecruiterIds)
                            ? apiData.assignedRecruiterIds
                            : apiData.recruiter_id ? [apiData.recruiter_id] : [],
                        assignedTlId: apiData.assigned_tl_id || apiData.assignedTlId,
                        /* ── CHANGE: use ddr_recruiter_name (correct field from SQL) ── */
                        recruiterNames: Array.isArray(apiData.recruiterNames)
                            ? apiData.recruiterNames
                            : apiData.ddr_recruiter_name ? [apiData.ddr_recruiter_name] : [],
                        company_process: apiData.company_process,
                        demandStatus: apiData.demand_status || apiData.demandStatus,
                        remarks: apiData.remarks,
                        assessmentRequired: (() => {
                            const val = apiData.assessment_required ?? apiData.assessmentRequired;
                            return (val === true) ? "yes" : (val === false) ? "no" : "no";
                        })(),
                        officeDays: apiData.office_days || apiData.officeDays || null,
                        assessmentLink: apiData.assessment_link || apiData.assessmentLink,
                        job_desc: apiData.job_desc,
                        role_specific_requirement: {
                            question1: apiData.role_specific_requirement?.question1 || null,
                            question2: apiData.role_specific_requirement?.question2 || null,
                            question3: apiData.role_specific_requirement?.question3 || null,
                            question4: apiData.role_specific_requirement?.question4 || null,
                            question5: apiData.role_specific_requirement?.question5 || null,
                        },
                        roleQuestions: Array.isArray(apiData.roleQuestions)
                            ? apiData.roleQuestions
                            : Object.values(apiData.role_specific_requirement || {}).filter(Boolean),
                        primaryIndex: "0",
                        openings: apiData.openings || null,
                        sub_account: apiData.sub_account || null,
                        end_client: apiData.end_client || null,
                        spoc_name: apiData.spoc_name || null,
                        timezone: apiData.timezone || null,
                        demand_category: apiData.demand_category || null,
                        received_date: apiData.received_date || apiData.receivedDate || null,
                        miscellaneous_file: apiData.miscellaneous_file || apiData.miscellaneousFile || null,
                    };

                    setData({
                        company_name: formattedData.company_name,
                        role_title: formattedData.role_title,
                        job_type: formattedData.job_type,
                        // job_code: apiData.job_code || "",
                        job_code: formattedData.job_code,
                        /* ── CHANGE: primary_skills stored as UUID array ── */
                        primary_skills: resolvedPrimarySkills.map(s => s.id),
                        other_skills: resolvedOtherSkills.map(s => s.id),
                        location_type: formattedData.locationType,
                        location: formattedData.location,
                        min_yrs: formattedData.min_yrs,
                        max_yrs: formattedData.max_yrs,
                        notice_period: formattedData.noticePeriod,
                        min_ctc: formattedData.minCtc,
                        max_ctc: formattedData.maxCtc,
                        company_process: formattedData.company_process,
                        assigned_recruiter_id: formattedData.assignedRecruiterIds,
                        assigned_tl_id: formattedData.assignedTlId,
                        demand_status: formattedData.demandStatus,
                        remarks: formattedData.remarks,
                        assessment_link: formattedData.assessmentLink,
                        job_desc: formattedData.job_desc,
                        otherSkill_dummy: formattedData.otherSkillDummy,
                        /* ── CHANGE: skill1-5 set from resolved UUIDs ── */
                        skill1: resolvedPrimarySkills?.[0]?.id || null,
                        skill2: resolvedPrimarySkills?.[1]?.id || null,
                        skill3: resolvedPrimarySkills?.[2]?.id || null,
                        skill4: resolvedPrimarySkills?.[3]?.id || null,
                        skill5: resolvedPrimarySkills?.[4]?.id || null,
                        /* ── CHANGE: otherSkills stored as name array for display ── */
                        otherSkills: resolvedOtherSkills.map(s => s.name),
                        question1: formattedData.role_specific_requirement.question1,
                        question2: formattedData.role_specific_requirement.question2,
                        question3: formattedData.role_specific_requirement.question3,
                        question4: formattedData.role_specific_requirement.question4,
                        question5: formattedData.role_specific_requirement.question5,
                        role_questions: formattedData.roleQuestions,
                        recruiter_name: formattedData.recruiterNames,
                        openings: formattedData.openings,
                        assessment_required: formattedData.assessmentRequired,
                        office_days: formattedData.officeDays,
                        sub_account: formattedData.sub_account,
                        end_client: formattedData.end_client,
                        spoc_name: formattedData.spoc_name,
                        timezone: formattedData.timezone,
                        demand_category: formattedData.demand_category,
                        received_date: formattedData.received_date,
                        miscellaneous_file: formattedData.miscellaneous_file,
                        notice_period_immediate: formattedData.noticePeriod === 'Immediate' || false,
                    });

                    setDataObj(formattedData);
                    console.log("[DEBUG-A] fetchDemandDetails setting job_type to:", formattedData.job_type);
                    if (formattedData.job_desc) {
                        setJDName(formattedData.job_desc.split("/").pop());
                    }
                } else {
                    throw new Error("Invalid response format");
                }
            } catch (error) {
                console.error("Error fetching demand details:", error);
                notification.error({ message: "Error", description: "Failed to fetch demand details: " + (error.message || "Unknown error") });
            }
        };

        fetchDemandDetails();
        /* ── CHANGE: depends on filterList so it only runs once skills are loaded ── */
    }, [filterList, demandTableId, recruiterTableId]);




    const [data, setData] = useState(() => {
        const queryData = {
            company_name: null, role_title: null, job_type: null, job_code: null,
            primary_skills: [], other_skills: [], location_type: null,
            min_yrs_month: null, min_yrs_year: null, min_yrs: null,
            max_yrs_month: null, max_yrs_year: null, max_yrs: null,
            notice_period: null, min_ctc: null, max_ctc: null,
            company_process: null, assigned_recruiter_id: [],
            assigned_tl_id: null, remarks: null, location: [],
            assessment_link: null, otherSkill_dummy: [], job_desc: null,
            primaryIndex: 0, skill1: null, skill2: null, skill3: null,
            skill4: null, skill5: null, otherSkills: [],
            question1: null, question2: null, question3: null,
            question4: null, question5: null, role_questions: [],
            recruiter_name: null, sales_actor_name: null, sales_actor: null,
            assessment_required: "no", office_days: null,
            sub_account: null, end_client: null, spoc_name: null,
            timezone: null,
            demand_category: null,
            received_date: null,
            miscellaneous_file: null,
            notice_period_immediate: false,
        };
        if (queryParams && Object.keys(queryParams).length !== 0) {
            for (const [key, value] of Object.entries(queryParams)) {
                if (key === "assessment_required" && typeof value === "boolean") {
                    queryData[key] = value ? "yes" : "no";
                } else if (key.includes("[]")) {
                    queryData[key.replace("[]", "")] = queryParams[key];
                } else {
                    queryData[key] = queryParams[key];
                }
            }
        }
        return queryData;
    });

    function debounce(func, wait) {
        let timeout;
        return function (...args) {
            const context = this;
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => { timeout = null; func.apply(context, args); }, wait);
        };
    }

    const apiCall = async (path, data, type) => {
        let res;
        if (type === "post") res = await functionService.post(path, data);
        if (type === "get") res = await functionService.get(path + data);
        const apiData = path === "recruiter/locations?name=" ? res?.response?.data?.data?.location : res?.response?.data?.data?.skills;
        if (res.status === true && apiData) {
            return apiData.length === 0 ? [] : apiData.map((item, index) => ({ key: index, value: item.name, test_available: item.test_available, ...item }));
        }
        return [];
    };

    const handleAutoCompleteSearch = async (fieldName, val, index) => {
        let res;
        switch (fieldName) {
            case "location":
                res = await apiCall("recruiter/locations?name=", val, "get");
                setLocationOptions(res); break;
            case "skill":
                res = await apiCall("recruiter/skills?name=", val, "get");
                setSkillOptions(res); break;
            case "otherSkills_all":
                res = await apiCall("recruiter/skills?name=", val, "get");
                setOtherSkillOptions(res); break;
            default: break;
        }
    };

    const debouncedSearch = useCallback(debounce(handleAutoCompleteSearch, 500), []);

    const changeData = (e) => {
        let id = e?.target?.id || "";
        let value = e?.target?.value || "";
        if (id === "location_type") setData({ ...data, [id]: value.toUpperCase() });
        else setData({ ...data, [id]: value });
        setError("");
    };

    const uploadHandler = async (event, fieldName = "job_desc") => {
        if (!event.target.files?.length) return;
        const fileData = event.target.files[0];
        const maxSize = 2 * 1024 * 1024;
        if (fileData.size > maxSize) {
            notification.error({ message: "File size exceeded", description: "File size can't be more than 2 MB!" });
            event.target.value = ""; return;
        }
        if (fileData.type !== "application/pdf" && !fileData.name.toLowerCase().endsWith(".pdf")) {
            notification.error({ message: "Unsupported file format", description: "Only PDF files are allowed" });
            event.target.value = ""; return;
        }
        try {
            let res = await functionService.uploadFile(fileData);
            if (res.status === true) {
                const uploadedLocation = res?.response?.data?.location;
                setData((prev) => ({ ...prev, [fieldName]: uploadedLocation }));
                if (fieldName === "job_desc") setJDName(fileData.name);
                if (fieldName === "miscellaneous_file") setMiscellaneousFileName(fileData.name);
            }
            else notification.error({ message: "Upload failed", description: "Something went wrong while uploading" });
        } catch (error) {
            notification.error({ message: "Upload error", description: "Unable to upload file" });
        }
    };

    const isUpdateMode = demandTableId && recruiterTableId && isCreatedToday(createdAt);
    const isNewDemandMode = demandTableId && recruiterTableId && !isCreatedToday(createdAt);

    /* ── Skill ID resolution helpers ──────────────────────────────────────── */
    const isUUID = (val) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);

    /* ── CHANGE: resolveSkillId handles both UUIDs and custom typed strings ── */
    const resolveSkillId = async (skillVal) => {
        if (!skillVal) return null;
        if (isUUID(skillVal)) return skillVal;
        const match = filterList.find(s => s.name?.toLowerCase() === String(skillVal).toLowerCase());
        if (match) return match.id;
        try {                                                                          // ← NEW
            const r = await functionService.post("demand/add-skill", { name: skillVal });
            console.log("add-skill full response:", JSON.stringify(r));  // ← add this
            const newId = Array.isArray(r?.response?.data?.data)
                ? r?.response?.data?.data?.[0]?.id
                : r?.response?.data?.data?.id || r?.response?.data?.id;
            if (!newId) throw new Error(`Failed to register skill: ${skillVal}`);
            return newId;
        } catch (e) {                                                                  // ← NEW
            notification.error({ message: "Skill Error", description: e.message });   // ← NEW
            return null;                                                               // ← NEW
        }                                                                              // ← NEW
    };

    const saveData = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setLoading(true);

        const err = validateForm();

        // ── FIX: bail immediately if validation failed ──
        if (err !== "") {
            setError(err);
            submittingRef.current = false;
            setLoading(false);
            return;
        }

        // Only reach here if form is valid
        const resolvedPrimarySkills = (
            await Promise.all((data.primary_skills || []).map(resolveSkillId))
        ).filter(Boolean);

        const resolvedOtherSkills = (
            await Promise.all((data.otherSkills || []).map(resolveSkillId))  // ← otherSkills (names), not other_skills (IDs)
        ).filter(Boolean);

        // Fresh demand
        if (err === "" && demandTableId === null && recruiterTableId === null) {
            try {
                const formattedData = buildFormattedDataForCreate(
                    data.assigned_recruiter_id,
                    resolvedPrimarySkills,
                    resolvedOtherSkills
                );
                const res = await functionService.post("demand/demand-details-recruiter", formattedData);
                if (res?.status === true && res?.response?.data?.meta?.code === 200 && res?.response?.data?.meta?.success === true) {
                    notification.success({ message: "Demand Created", description: `Successfully created demand with ${data.assigned_recruiter_id.length} recruiter(s)` });
                    setFormSubmitted(true);
                    navigate(`/confirm-demand`, { replace: true });
                } else {
                    throw new Error(res?.response?.data?.meta?.message || "Failed to create demand");
                }
            } catch (error) {
                notification.error({ message: "Creation Failed", description: error.message || "Failed to create demand. Please try again." });
            } finally { submittingRef.current = false; setLoading(false); }
        }

        // UPDATE mode — created today
        else if (isUpdateMode) {
            try {
                const formattedData = buildFormattedDataForUpdate(resolvedPrimarySkills, resolvedOtherSkills);
                console.log("[DEBUG] formattedData.jobType:", formattedData.jobType);
                console.log("[DEBUG] formattedData keys:", Object.keys(formattedData));
                const obj = {
                    demand_id: demandTableId,
                    recruiter_id: recruiterTableId,
                    dataToUpdate: formattedData,
                    recruiter_ids: data.assigned_recruiter_id,
                };
                const res = await functionService.updateTodayDemand(obj);
                if (res?.status === true && res?.response?.data?.meta?.code === 200 && res?.response?.data?.meta?.success === true) {
                    notification.success({ message: "Demand Updated", description: "Demand updated successfully" });
                    setFormSubmitted(true);
                    navigate(`/confirm-demand`, { replace: true });
                } else {
                    throw new Error(res?.response?.data?.meta?.message || "Failed to update demand");
                }
            } catch (error) {
                notification.error({ message: "Update Failed", description: error.message || "Failed to update demand. Please try again." });
            } finally {
                submittingRef.current = false;
                localStorage.removeItem("demand_table_id");
                localStorage.removeItem("demand_recruiter_id");
                setLoading(false);
            }
        }

        // NEW DEMAND mode — not created today
        else if (isNewDemandMode) {
            try {
                const formattedData = buildFormattedDataForCreate(
                    data.assigned_recruiter_id,
                    resolvedPrimarySkills,
                    resolvedOtherSkills
                );
                const res = await functionService.post("demand/demand-details-recruiter", formattedData);
                if (res?.status === true && res?.response?.data?.meta?.code === 200 && res?.response?.data?.meta?.success === true) {
                    notification.success({ message: "Demand Created", description: `Successfully created new demand with ${data.assigned_recruiter_id.length} recruiter(s)` });
                    setFormSubmitted(true);
                    navigate(`/confirm-demand`, { replace: true });
                } else {
                    throw new Error(res?.response?.data?.meta?.message || "Failed to create demand");
                }
            } catch (error) {
                notification.error({ message: "Creation Failed", description: error.message || "Failed to create demand. Please try again." });
            } finally { submittingRef.current = false; setLoading(false); }
        }

        else { submittingRef.current = false; setLoading(false); setError(err); }
    };

    const buildFormattedDataForCreate = (recruiterIds, resolvedPrimarySkills, resolvedOtherSkills) => ({
        assessmentLink: data.assessment_link || null,
        assignedRecruiterIds: recruiterIds || [],
        assignedTlId: data.assigned_tl_id,
        company_name: data.company_name.trim(),
        company_process: data.company_process,
        demandStatus: data.demand_status,
        job_desc: data.job_desc,
        jobType: data.job_type,
        job_code: data.job_code,
        location: (data.location || []).map(loc => typeof loc === "string" ? loc.replace(/[{}"]/g, "").trim() : loc),
        locationType: data.location_type,
        maxCtc: data.max_ctc != null ? data.max_ctc.toString() : null,
        minCtc: data.min_ctc != null ? data.min_ctc.toString() : null,
        max_yrs: data.max_yrs != null ? data.max_yrs.toString() : null,
        min_yrs: data.min_yrs != null ? data.min_yrs.toString() : null,
        noticePeriod: data.notice_period_immediate
            ? "Immediate"
            : data.notice_period
                ? `${String(data.notice_period).replace(/\s*days?\s*$/i, "").trim()} days`
                : null,
        otherSkillDummy: data.otherSkill_dummy || [],
        otherSkills: resolvedOtherSkills || [],
        primaryIndex: "0",
        primarySkills: resolvedPrimarySkills || [],
        role_specific_requirement: { question1: data.question1 || null, question2: data.question2 || null, question3: data.question3 || null, question4: data.question4 || null, question5: data.question5 || null },
        recruiterNames: data.recruiter_name || [],
        remarks: data.remarks,
        roleQuestions: data.role_questions || [],
        role_title: data.role_title.trim(),
        primary_skills: resolvedPrimarySkills || [],
        openings: data.openings || null,
        assessmentRequired: data.assessment_required,
        officeDays: data.office_days,
        sub_account: data.sub_account || null,
        end_client: data.end_client || null,
        spoc_name: data.spoc_name || null,
        timezone: data.timezone || null,
        demand_category: data.demand_category || null,
        received_date: data.received_date || null,
        miscellaneous_file: data.miscellaneous_file || null,
    });

    const buildFormattedDataForUpdate = (resolvedPrimarySkills, resolvedOtherSkills) => ({
        assessmentLink: data.assessment_link || null,
        new_recruiter_id: data.assigned_recruiter_id[0] || [],
        assignedTlId: data.assigned_tl_id,
        company_name: data.company_name.trim(),
        company_process: data.company_process,
        demandStatus: data.demand_status,
        job_desc: data.job_desc,
        jobType: data.job_type,
        job_code: data.job_code,
        location: Array.isArray(data.location) ? data.location : data.location ? [data.location] : [],
        locationType: data.location_type,
        max_ctc: data.max_ctc != null ? data.max_ctc.toString() : null,
        max_yrs: data.max_yrs != null ? data.max_yrs.toString() : null,
        maxYearsMonth: data.max_yrs_month || null,
        maxYearsYear: data.max_yrs_year != null ? data.max_yrs_year.toString() : null,
        min_ctc: data.min_ctc != null ? data.min_ctc.toString() : null,
        min_yrs: data.min_yrs != null ? data.min_yrs.toString() : null,
        minYearsMonth: data.min_yrs_month || null,
        minYearsYear: data.min_yrs_year != null ? data.min_yrs_year.toString() : null,
        noticePeriod: data.notice_period_immediate ? "Immediate" : (data.notice_period ? `${data.notice_period} days` : null),
        otherSkillDummy: data.otherSkill_dummy || [],
        otherSkills: resolvedOtherSkills || [],
        primaryIndex: "0",
        primarySkills: resolvedPrimarySkills || [],
        role_specific_requirement: { question1: data.question1 || null, question2: data.question2 || null, question3: data.question3 || null, question4: data.question4 || null, question5: data.question5 || null },
        recruiter_name: data.recruiter_name?.[0] || [],
        remarks: data.remarks,
        roleQuestions: data.role_questions || [],
        role_title: data.role_title.trim(),
        salesActor: data.sales_actor,
        sales_actor_name: data.sales_actor_name,
        primary_skills: resolvedPrimarySkills || [],
        sales_actor: data.sales_actor,
        openings: data.openings || null,
        assessmentRequired: data.assessment_required,
        officeDays: data.office_days,
        sub_account: data.sub_account || null,
        end_client: data.end_client || null,
        spoc_name: data.spoc_name || null,
        timezone: data.timezone || null,
        demand_category: data.demand_category || null,
        received_date: data.received_date || null,
        miscellaneous_file: data.miscellaneous_file || null,
    });

    const handleSubmitClick = () => { saveData(); };

    const recruiterSearch = async () => {
        let res = await functionService.get(`demand/recruiters-list`);
        let apiData = res?.response?.data?.data;
        const optionsWithNames = apiData.map((item) => ({ ...item, recruiter_name: item.label }));
        setRecruiterOptions(optionsWithNames);
    };

    const TLSearch = async () => {
        let res = await functionService.get(`demand/recruiters-tl-list`);
        let apiData = res?.response?.data?.data;
        setTLOptions(apiData);
    };

    /* ── CHANGE: skillSearch now also returns the result so callers can await it ── */
    const skillSearch = async () => {
        let res = await functionService.get(`recruiter/skills?name=`);
        let rawSkills = res?.response?.data?.data?.skills || [];

        console.log("RAW SKILL SAMPLE:", rawSkills[0]); // ← add this

        let apiData = rawSkills.map(s => ({
            ...s,
            id: s.id ?? s.value ?? s.name,
            name: s.name,
        }));

        console.log("MAPPED SKILL SAMPLE:", apiData[0]); // ← and this
        console.log("TARGET UUID TO FIND:", "f962c5a7-7543-4a21-bf13-11744a3d811d");
        console.log("FOUND?", apiData.find(s => s.id === "f962c5a7-7543-4a21-bf13-11744a3d811d"));

        allSkillsRef.current = apiData;
        setSkillOptions(apiData);
        setFilter(apiData);
        return apiData;
    };

    const onSkillSearch = (e) => {
        if (!e) { setFilter(allSkillsRef.current); return; }
        // Only update dropdown options, never shrink the master filterList
        setSkillOptions(allSkillsRef.current.filter(i =>
            i.name.toLowerCase().includes(e.toLowerCase())
        ));
        // filterList stays full — SkillPair needs all 223 skills for UUID resolution
    };

    const buildPrimarySkills = (upd) => ['skill1', 'skill2', 'skill3', 'skill4', 'skill5'].map(k => upd[k]).filter(Boolean);

    const skillUpdate = (skillKey, e) => {
        if (e) {
            const others = ['skill1', 'skill2', 'skill3', 'skill4', 'skill5'].filter(k => k !== skillKey);
            const toName = val => filterList.find(s => s.id === val)?.name?.toLowerCase() || String(val || "").toLowerCase();  // ← NEW
            if (others.some(k => data[k] && toName(data[k]) === toName(e))) {                                                   // ← NEW
                setErrorMessage('Please check, selected skills must be unique');
                return;
            }
        }
        setData(prev => {
            const updated = { ...prev, [skillKey]: e };
            const arr = ['skill1', 'skill2', 'skill3', 'skill4', 'skill5'].map(k => updated[k]).filter(Boolean);
            return { ...updated, primary_skills: arr };
        });
        setErrorMessage('');
    };

    const otherSkillsUpdate = (loc) => {
        if (data.primary_skills.length === 0) {
            setErrorMessage("Please fill Skill 1 first");
            return;
        }
        let otherArr = [];
        for (let i = 0; i < loc?.length; i++) {
            const skill = filterList.find(item => item.name === loc[i]);  // ← filterList instead of skillOptions
            if (skill) {
                if (data.primary_skills.includes(skill.id)) {
                    setErrorMessage("Other skills must be different from primary skills");
                    return;
                }
                otherArr.push(skill.id);
            } else {
                otherArr.push(loc[i]);  // ← NEW: keep raw name for new custom skills
            }
        }
        setData(prev => ({ ...prev, otherSkills: loc, other_skills: otherArr }));
        setError("");
        setErrorMessage("");
    };

    const question1Update = (e) => { setData(prev => { const arr = [e, ...[prev.question2, prev.question3, prev.question4, prev.question5].filter(Boolean)]; return { ...prev, question1: e, role_questions: arr }; }); };
    const question2Update = (e) => { setData(prev => { const arr = [...[prev.question1].filter(Boolean), e, ...[prev.question3, prev.question4, prev.question5].filter(Boolean)]; return { ...prev, question2: e, role_questions: arr }; }); };
    const question3Update = (e) => { setData(prev => { const arr = [...[prev.question1, prev.question2].filter(Boolean), e, ...[prev.question4, prev.question5].filter(Boolean)]; return { ...prev, question3: e, role_questions: arr }; }); };
    const question4Update = (e) => { setData(prev => { const arr = [...[prev.question1, prev.question2, prev.question3].filter(Boolean), e, ...[prev.question5].filter(Boolean)]; return { ...prev, question4: e, role_questions: arr }; }); };
    const question5Update = (e) => { setData(prev => { const arr = [...[prev.question1, prev.question2, prev.question3, prev.question4].filter(Boolean), e]; return { ...prev, question5: e, role_questions: arr }; }); };

    let navigate = useNavigate();
    const [formSubmitted, setFormSubmitted] = useState(false);

    useEffect(() => {
        return () => {
            if (!formSubmitted) { localStorage.removeItem("demand_table_id"); localStorage.removeItem("demand_recruiter_id"); }
        };
    }, [formSubmitted]);

    /* ── CHANGE: run recruiterSearch and TLSearch on mount; skillSearch sets
       filterList which triggers the fetchDemandDetails effect above ── */
    useEffect(() => {
        recruiterSearch();
        TLSearch();
        skillSearch(); // when this resolves, filterList is set → triggers fetchDemandDetails
    }, []);

    useEffect(() => {
        if (dataObj) {
            const formattedData = {
                sales_actor: dataObj.salesActor || dataObj.sales_actor,
                company_name: dataObj.company_name,
                role_title: dataObj.role_title,
                job_code: dataObj.job_code || null,
                job_type: (dataObj.jobType || dataObj.job_type || "").toLowerCase(),
                primary_skills: (dataObj.primarySkills || []).map(s => s.id || s),
                other_skills: (dataObj.otherSkills || []).map(s => s.id || s),
                location_type: (dataObj.locationType || dataObj.location_type || "").toUpperCase(),
                location: Array.isArray(dataObj.location) ? dataObj.location : dataObj.location ? [dataObj.location] : [],
                min_ctc: dataObj.minCtc ? parseInt(dataObj.minCtc) : null,
                max_ctc: dataObj.maxCtc ? parseInt(dataObj.maxCtc) : null,
                notice_period: dataObj.noticePeriod || null,
                min_yrs_year: dataObj.min_yrs ? parseFloat(dataObj.min_yrs) : null,
                min_yrs: dataObj.min_yrs ? parseFloat(dataObj.min_yrs) : null,
                max_yrs_year: dataObj.max_yrs ? parseFloat(dataObj.max_yrs) : null,
                max_yrs: dataObj.max_yrs ? parseFloat(dataObj.max_yrs) : null,
                company_process: dataObj.company_process,
                assigned_recruiter_id: dataObj.assignedRecruiterIds,
                assigned_tl_id: dataObj.assignedTlId,
                demand_status: dataObj.demandStatus,
                remarks: dataObj.remarks,
                assessment_link: dataObj.assessmentLink,
                otherSkill_dummy: dataObj.otherSkillDummy || [],
                job_desc: dataObj.job_desc,
                primaryIndex: dataObj.primaryIndex || "0",
                /* ── CHANGE: skill1-5 from resolved primarySkills ── */
                skill1: dataObj.primarySkills?.[0]?.id || null,
                skill2: dataObj.primarySkills?.[1]?.id || null,
                skill3: dataObj.primarySkills?.[2]?.id || null,
                skill4: dataObj.primarySkills?.[3]?.id || null,
                skill5: dataObj.primarySkills?.[4]?.id || null,
                otherSkills: (dataObj.otherSkills || []).map(s => s.name || s),
                question1: dataObj.roleQuestions?.[0] || dataObj.role_specific_requirement?.question1 || null,
                question2: dataObj.roleQuestions?.[1] || dataObj.role_specific_requirement?.question2 || null,
                question3: dataObj.roleQuestions?.[2] || dataObj.role_specific_requirement?.question3 || null,
                question4: dataObj.roleQuestions?.[3] || dataObj.role_specific_requirement?.question4 || null,
                question5: dataObj.roleQuestions?.[4] || dataObj.role_specific_requirement?.question5 || null,
                role_questions: dataObj.roleQuestions || [],
                recruiter_name: dataObj.recruiterNames || [],
                sales_actor_name: dataObj.sales_actor_name,
                sales_actor: dataObj.sales_actor || dataObj.salesActor,
                openings: dataObj.openings || null,
                assessment_required: dataObj.assessmentRequired || "no",
                office_days: dataObj.officeDays || null,
                sub_account: dataObj.sub_account || null,
                end_client: dataObj.end_client || null,
                spoc_name: dataObj.spoc_name || null,
                timezone: dataObj.timezone || null,
                demand_category: dataObj.demand_category || null,
                received_date: dataObj.received_date || null,
                miscellaneous_file: dataObj.miscellaneous_file || null,
                notice_period_immediate: dataObj.noticePeriod === 'Immediate' || false,
            };
            setData(formattedData);
            console.log("[DEBUG-B] dataObj effect setting job_type to:", formattedData.job_type);
            if (dataObj.job_desc) setJDName(dataObj.job_desc.split("/").pop());
            if (dataObj.miscellaneous_file) setMiscellaneousFileName(dataObj.miscellaneous_file.split("/").pop());
        }
    }, [dataObj]);

    const validateForm = () => {
        const errs = {};
        if (!data.company_name) errs.company_name = "Account Name is required";
        if (!data.role_title) errs.role_title = "Role Title is required";
        if (!data.job_type) errs.job_type = "Please select a Job Type";
        if (!data.job_code?.trim()) {
            errs.job_code = "Please write a Job code";
        }
        if (!data.openings) errs.openings = "Number of Openings is required";

        if (!data.skill1) errs.skill1 = "At least one Primary Skill is required";
        if (!data.location_type) errs.location_type = "Please select a Location Type";
        if ((data.location_type === "ONSITE" || data.location_type === "HYBRID") && (!data.location || data.location.length === 0))
            errs.location = "Location is required for Onsite/Hybrid roles";
        if (!data.max_ctc) errs.max_ctc = "Maximum CTC is required";
        if (data.min_ctc && data.max_ctc && parseInt(data.min_ctc) >= parseInt(data.max_ctc))
            errs.max_ctc = "Max CTC must be greater than Min CTC";
        if (data.min_yrs_year && data.max_yrs_year && parseFloat(data.min_yrs_year) >= parseFloat(data.max_yrs_year))
            errs.max_yrs = "Max Yrs must be greater than Min Yrs";
        if (!data.job_desc) errs.job_desc = "Please upload a Job Description";
        if (!data.assigned_recruiter_id || data.assigned_recruiter_id.length === 0)
            errs.assigned_recruiter_id = "Please select at least one Recruiter";
        if (!data.assigned_tl_id) errs.assigned_tl_id = "Please select a Team Lead";
        if (data.location_type === "HYBRID" && !data.office_days)
            errs.office_days = "Please select Number of Days in Office";
        if (data.assessment_required === "yes" && !data.assessment_link)
            errs.assessment_link = "Assessment Link is required";
        if (!data.timezone)
            errs.timezone = "Time Zone is required";
        if (!data.demand_category)
            errs.demand_category = "Demand Category is required";
        setFieldErrors(errs);
        return Object.keys(errs).length === 0 ? "" : "Please fix the errors above";
    };

    const recruiterSelectRef = useRef(null);
    const allSkillsRef = useRef([]);
    const submittingRef = useRef(false);

    return (
        <>
            {loading ? <Loader loaderOpen={true} /> : ""}
            <Styled.MainDiv>
                <Sidemenu />
                <Styled.ColumnDiv>

                    <div className={Styles.container}>
                        <div className={Styles.heading_container}>
                            <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                <div>
                                    <p style={{ fontWeight: "700", fontSize: "28px", marginBottom: "16px" }}>
                                        New Demand
                                    </p>
                                </div>
                                {demandTableId && recruiterTableId && (
                                    <div style={{
                                        display: "flex", alignItems: "center",
                                        padding: "6px 16px", borderRadius: 20, fontSize: 13, fontWeight: 700,
                                        background: isUpdateMode ? "#e8f5e9" : "#fff3e0",
                                        color: isUpdateMode ? "#2e7d32" : "#e65100",
                                        border: `1px solid ${isUpdateMode ? "#a5d6a7" : "#ffcc80"}`,
                                        height: "fit-content", alignSelf: "center",
                                    }}>
                                        {isUpdateMode ? "✏️ Update Mode" : "➕ New Demand Mode"}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── Company Role Details ── */}
                        <div className={Styles.first_container} style={{ marginTop: "0px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <p className={Styles.first_container_heading} style={{ color: "#FF2C91", fontSize: "20px", marginTop: "0px" }}>
                                    Company Role Details
                                </p>
                                <div style={{ width: "22%", minWidth: "220px" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px", marginBottom: "6px", marginTop: "-10px" }}>Received Date <span style={{ fontWeight: 400, color: T.muted }}></span></p>
                                    <DatePicker
                                        style={{ width: "100%", height: T.inputH }}
                                        format={["DD-MM-YYYY", "DD/MM/YYYY", "DD-MM-YY", "DD/MM/YY", "DDMMYYYY"]}
                                        placeholder="DDMMYYYY"
                                        allowClear
                                        value={data.received_date ? dayjs(data.received_date) : null}
                                        disabledDate={(current) => current && current.endOf("day").isAfter(dayjs().endOf("day"))}
                                        onChange={(_, dateString) => {
                                            if (!dateString || dateString.trim() === "") {
                                                setData(prev => ({ ...prev, received_date: null }));
                                            } else {
                                                const formats = ["DD-MM-YYYY", "DD/MM/YYYY", "DD-MM-YY", "DD/MM/YY", "DDMMYYYY"];
                                                let parsedDate = null;
                                                for (const fmt of formats) {
                                                    parsedDate = dayjs(dateString, fmt, true);
                                                    if (parsedDate.isValid()) break;
                                                }
                                                if (parsedDate && parsedDate.isValid() && !parsedDate.isAfter(dayjs(), "day")) {
                                                    setData(prev => ({ ...prev, received_date: parsedDate.utc().format() }));
                                                }
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const dateString = e?.target?.value;
                                            if (dateString && dateString.trim() !== "") {
                                                const formats = ["DD-MM-YYYY", "DD/MM/YYYY", "DD-MM-YY", "DD/MM/YY", "DDMMYYYY"];
                                                let parsedDate = null;
                                                for (const fmt of formats) {
                                                    parsedDate = dayjs(dateString, fmt, true);
                                                    if (parsedDate.isValid()) break;
                                                }
                                                if (parsedDate && parsedDate.isValid() && !parsedDate.isAfter(dayjs(), "day")) {
                                                    setData(prev => ({ ...prev, received_date: parsedDate.utc().format() }));
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            <p style={{ display: "flex", marginTop: "0px", justifyContent: "space-between", flexWrap: "wrap", width: "100%", gap: "20px" }}>

                                {/* Account Name */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Account Name<span style={{ color: "#f04638" }}>*</span></p>
                                    <div className={Styles.expectedStartDateContainer}>
                                        <input
                                            className={Styles.digit_input}
                                            value={data.company_name || ""}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                setData(prev => ({ ...prev, company_name: value }));
                                                // In Today-Demand update mode, skip setDataObj: it re-triggers the
                                                // [dataObj] effect, which resets ALL of `data` (including the
                                                // recruiter selection) back to the originally loaded values.
                                                if (dataObj && !isUpdateMode) setDataObj(prev => ({ ...prev, company_name: value }));
                                                setError("");
                                            }}
                                            placeholder="Type account name"
                                            style={{ textTransform: "uppercase" }}
                                        />
                                    </div>
                                    <FieldError field="company_name" />
                                </div>

                                {/* Role Title */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Role Title<span style={{ color: "#f04638" }}>*</span></p>
                                    <div className={Styles.expectedStartDateContainer}>
                                        <AutoComplete
                                            value={data.role_title || ""}
                                            options={roleTitleOptions}
                                            onSearch={async (val) => {
                                                if (!val) { setRoleTitleOptions([]); return; }
                                                const res = await functionService.get(`demand/job-role-suggestion?job_role_suggestion=${val.toLowerCase()}`);
                                                const apiData = res?.response?.data?.data;
                                                if (Array.isArray(apiData)) {
                                                    setRoleTitleOptions(apiData.map(item => ({ value: item.job_role.toUpperCase(), label: item.job_role.toUpperCase() })));
                                                }
                                            }}
                                            onChange={(val) => { setData({ ...data, role_title: val.toUpperCase() }); setError(""); }}
                                            onSelect={(val) => { setData({ ...data, role_title: val.toUpperCase() }); setError(""); }}
                                            placeholder="Type role"
                                            style={{ width: "100%", textTransform: "uppercase" }}
                                            filterOption={false}
                                            allowClear
                                        />
                                    </div>
                                    <FieldError field="role_title" />
                                </div>

                                {/* Sub-Account */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Sub-Account</p>
                                    <div className={Styles.expectedStartDateContainer}>
                                        <input
                                            className={Styles.digit_input}
                                            value={data.sub_account || ""}
                                            onChange={(e) => { setData(prev => ({ ...prev, sub_account: e.target.value || null })); setError(""); }}
                                            placeholder="Type sub-account"
                                        />
                                    </div>
                                </div>

                                {/* End Client */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>End Client</p>
                                    <div className={Styles.expectedStartDateContainer}>
                                        <input
                                            className={Styles.digit_input}
                                            value={data.end_client || ""}
                                            onChange={(e) => { setData(prev => ({ ...prev, end_client: e.target.value || null })); setError(""); }}
                                            placeholder="Type end client"
                                        />
                                    </div>
                                </div>
                                {/* Single Point of Contact */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>SPOC</p>
                                    <div className={Styles.expectedStartDateContainer}>
                                        <input
                                            className={Styles.digit_input}
                                            value={data.spoc_name || ""}
                                            onChange={(e) => { setData(prev => ({ ...prev, spoc_name: e.target.value || null })); setError(""); }}
                                            placeholder="Type SPOC name"
                                        />
                                    </div>
                                </div>
                                {/* Demand Category */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Demand Category<span style={{ color: "#f04638" }}>*</span></p>
                                    <div className={Styles.expectedStartDateContainer}>
                                        <Select placeholder="Select demand category" optionFilterProp="children" value={data.demand_category}
                                            onChange={(e) => { setData({ ...data, demand_category: e }); setError(""); clearFieldError("demand_category"); }}>
                                            {demandCategoryOptions?.map(item => <Select.Option key={item.key} value={item.key}>{item.value}</Select.Option>)}
                                        </Select>
                                    </div>
                                    <FieldError field="demand_category" />
                                </div>

                                {/* Job Code */}
                                <div style={{ width: "48%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>
                                        Job Code<span style={{ color: "#f04638" }}>*</span>
                                    </p>

                                    <div className={Styles.expectedStartDateContainer}>
                                        <input
                                            type="text"
                                            placeholder="Enter Job Code"
                                            value={data.job_code || ""}
                                            maxLength={64}
                                            onChange={(e) => {
                                                const value = e.target.value.trim();

                                                setData({
                                                    ...data,
                                                    job_code: value.toUpperCase(),
                                                });

                                                setError("");
                                            }}
                                        />
                                    </div>

                                    <FieldError field="job_code" />
                                </div>

                                {/* Job Type */}
                                <div style={{ width: "48%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>
                                        Job Type
                                        <span style={{ color: "#f04638" }}>*</span>
                                    </p>

                                    <div className={Styles.expectedStartDateContainer}>
                                        <Select

                                            placeholder="Select job type"
                                            optionFilterProp="children"
                                            value={data.job_type}
                                            onChange={(value) => {
                                                setData({
                                                    ...data,
                                                    job_type: value,
                                                });

                                                setError("");
                                            }}
                                        >
                                            {jobTypeOptions?.map((item) => (
                                                <Select.Option
                                                    key={item.key}
                                                    value={item.key}
                                                >
                                                    {item.value}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </div>

                                    <FieldError field="job_type" />
                                </div>

                                {/* Min Yrs */}
                                <div style={{ width: "48%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Min Yrs of Exp<span style={{ color: "#f04638" }}>*</span></p>
                                    <Input type="number" step="0.1" placeholder="e.g., 2.3" value={data.min_yrs_year}
                                        onChange={(e) => {
                                            const raw = e.target.value;

                                            if (raw === "") {
                                                setData({
                                                    ...data,
                                                    min_yrs_year: null,
                                                    min_yrs: null,
                                                });
                                                return;
                                            }
                                            const value = Number(parseFloat(raw).toFixed(1));
                                            setData({ ...data, min_yrs_year: value, min_yrs: value });
                                            if (data.max_yrs_year && value >= data.max_yrs_year) setError("Max Yrs of Exp must be greater than Min Yrs of Exp");
                                            else setError("");
                                        }}
                                        style={{ fontWeight: "400", fontSize: "18px", width: "100%", height: "53px" }}
                                    />
                                </div>

                                {/* Max Yrs */}
                                <div style={{ width: "48%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Max Yrs of Exp</p>
                                    <Input type="number" step="0.1" placeholder="e.g., 5.5" value={data.max_yrs_year}
                                        onChange={(e) => {
                                            const raw = e.target.value;

                                            if (raw === "") {
                                                setData({
                                                    ...data,
                                                    max_yrs_year: null,
                                                    max_yrs: null,
                                                });
                                                return;
                                            }
                                            const value = Number(parseFloat(raw).toFixed(1));
                                            setData({ ...data, max_yrs_year: value, max_yrs: value });
                                            if (data.min_yrs_year && value <= data.min_yrs_year) setError("Max Yrs of Exp must be greater than Min Yrs of Exp");
                                            else setError("");
                                        }}
                                        style={{ fontWeight: "400", fontSize: "18px", width: "100%", height: "53px" }}
                                    />
                                    <FieldError field="max_yrs" />
                                </div>

                                {/* Min CTC */}
                                <div style={{ width: "48%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Min CTC</p>
                                    <div className={Styles.digit_label}>
                                        <input type="number" step="0.1" value={data.min_ctc || ""} onChange={(e) => {
                                            const raw = e.target.value;

                                            if (raw === "") {
                                                setData({ ...data, min_ctc: null });
                                                return;
                                            }

                                            const value = Number(parseFloat(raw).toFixed(1));

                                            setData({ ...data, min_ctc: value });

                                            if (data.max_ctc && value >= data.max_ctc)
                                                setError("Max CTC must be greater than Min CTC");
                                            else
                                                setError("");
                                        }} placeholder="Type CTC" />
                                        <div>LPA</div>
                                    </div>
                                </div>

                                {/* Max CTC */}
                                <div style={{ width: "48%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Max CTC<span style={{ color: "#f04638" }}>*</span></p>
                                    <div className={Styles.digit_label}>
                                        <input type="number" step="0.1" value={data.max_ctc || ""} onChange={(e) => {
                                            const raw = e.target.value;

                                            if (raw === "") {
                                                setData({ ...data, max_ctc: null });
                                                return;
                                            }

                                            const value = Number(parseFloat(raw).toFixed(1));

                                            setData({ ...data, max_ctc: value });

                                            if (data.min_ctc && value <= data.min_ctc)
                                                setError("Max CTC must be greater than Min CTC");
                                            else
                                                setError("");
                                        }} placeholder="Type CTC" />
                                        <div>LPA</div>
                                    </div>
                                    <FieldError field="max_ctc" />
                                </div>
                                {/* Openings */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>
                                        Number of Openings
                                        <span style={{ color: "#f04638" }}>*</span>
                                    </p>
                                    <div className={Styles.expectedStartDateContainer}>
                                        <input className={Styles.digit_input} value={data.openings || ""} onChange={(e) => { setData({ ...data, openings: e.target.value ? parseInt(e.target.value) : null }); setError(""); }} placeholder="Type Number of Openings" />
                                    </div>
                                    <FieldError field="openings" />
                                </div>

                                {/* Notice Period */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Notice Period Allowed</p>
                                    <div style={{ display: "flex", alignItems: "center", border: "1px solid #c9c9c9", borderRadius: 8, overflow: "hidden", background: data.notice_period_immediate ? "#f5f5f5" : "#fff", height: 46 }}>
                                        <input
                                            type="text"
                                            value={data.notice_period_immediate ? "" : (data.notice_period || "")}
                                            readOnly={data.notice_period_immediate}
                                            onChange={e => {
                                                if (data.notice_period_immediate) return;
                                                const v = e.target.value;
                                                if (/^\d*$/.test(v)) {
                                                    setData(prev => ({ ...prev, notice_period: v === "" ? null : v }));
                                                    setError("");
                                                }
                                            }}
                                            placeholder={data.notice_period_immediate ? "" : "e.g. 30"}
                                            style={{ flex: 1, border: "none", outline: "none", fontSize: 14, padding: "0 12px", background: "transparent", color: data.notice_period_immediate ? "#bbb" : "#1E2228", cursor: data.notice_period_immediate ? "not-allowed" : "text" }}
                                        />
                                        <div style={{ borderLeft: "1px solid #c9c9c9", padding: "0 12px", fontSize: 13, color: "#939599", height: "100%", display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>Days</div>
                                    </div>
                                    <div style={{ marginTop: 8 }}>
                                        <Checkbox
                                            checked={data.notice_period_immediate === true}
                                            onChange={e => setData(prev => ({
                                                ...prev,
                                                notice_period_immediate: e.target.checked,
                                                notice_period: e.target.checked ? null : prev.notice_period,
                                            }))}
                                        >
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>Immediate</span>
                                        </Checkbox>
                                    </div>
                                </div>

                                {/* Timezone */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>
                                        Time Zone<span style={{ color: "#f04638" }}>*</span>
                                    </p>
                                    <input
                                        type="text"
                                        value={data.timezone || ""}
                                        onChange={e => { setData(prev => ({ ...prev, timezone: e.target.value || null })); clearFieldError("timezone"); }}
                                        placeholder="e.g. IST, EST, UTC+5:30"
                                        style={{ width: "100%", fontSize: 14, border: fieldErrors.timezone ? "1px solid #f04638" : "1px solid #c9c9c9", borderRadius: 8, padding: "0 14px", height: 46, boxSizing: "border-box", outline: "none", background: "#fff", color: "#1E2228", fontFamily: "inherit" }}
                                    />
                                    <FieldError field="timezone" />
                                </div>

                                {/* Location Type */}
                                <div style={{ border: "2px", width: "34%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Location type<span style={{ color: "#f04638" }}>*</span></p>
                                    <Styled.RoleWrapDiv style={{ width: "100%" }}>
                                        <label style={{ display: "flex", alignItems: "center" }}>
                                            <input id="location_type" onChange={changeData} type="radio" name="role" checked={data.location_type === "REMOTE"} value="REMOTE" />Remote
                                        </label>
                                        <label style={{ display: "flex", alignItems: "center" }}>
                                            <input id="location_type" onChange={changeData} type="radio" name="role" checked={data.location_type === "HYBRID"} value="HYBRID" />Hybrid
                                        </label>
                                        <label style={{ display: "flex", alignItems: "center" }}>
                                            <input id="location_type" onChange={changeData} type="radio" name="role" checked={data.location_type === "ONSITE"} value="ONSITE" />On-Site
                                        </label>
                                    </Styled.RoleWrapDiv>
                                    <FieldError field="location_type" />
                                </div>

                                {(data.location_type === "ONSITE" || data.location_type === "HYBRID") && (
                                    <div style={{ display: "flex", flexDirection: "column", marginTop: "-8px", width: "31%" }}>
                                        <Styled.HeadingDiv>Location {data.location_type !== "REMOTE" ? <span>*</span> : ""}</Styled.HeadingDiv>
                                        <Styled.AutosearchDiv style={{ width: "100%" }}>
                                            <AutoSearchBox onSearch={(fieldName, val) => debouncedSearch(fieldName, val)} options={locationsOptions} fieldName="location" selectedValues={data.location} setSelectedValues={(loc) => { setData({ ...data, location: loc }); setError(""); }} placeholder="Select Location" />
                                        </Styled.AutosearchDiv>
                                        <FieldError field="location" />
                                    </div>
                                )}

                                {data.location_type === "HYBRID" && (
                                    <div style={{ width: "31%" }}>
                                        <p style={{ fontWeight: "700", fontSize: "16px" }}>Number of Days in Office<span style={{ color: "#f04638" }}>*</span></p>
                                        <div className={Styles.expectedStartDateContainer}>
                                            <Select placeholder="Select days" optionFilterProp="children" value={data.office_days} onChange={(e) => { setData({ ...data, office_days: e }); setError(""); }}>
                                                {officeDaysOptions?.map(item => <Select.Option key={item.key} value={item.key}>{item.value}</Select.Option>)}
                                            </Select>
                                        </div>
                                    </div>
                                )}

                                {/* File Uploads */}
                                <div style={{ width: "100%", display: "flex", justifyContent: "space-between", gap: "20px" }}>
                                    <div style={{ border: "2px", width: "48%" }}>
                                        <p style={{ fontWeight: "700", fontSize: "16px" }}>Job Description<span style={{ color: "#f04638" }}>*</span></p>
                                        {JDName ? (
                                            <p style={{ fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "left", maxWidth: "220px" }}>
                                                {JDName.length > 20 ? `${JDName.slice(0, 20)}...` : JDName}
                                                <img style={{ marginLeft: "10px", cursor: "pointer" }} onClick={() => { setJDName(null); setData(prev => ({ ...prev, job_desc: null })); }} width={12} height={12} src={close} alt="Remove" />
                                            </p>
                                        ) : (
                                            <div className={Styles.expectedStartDateContainer}>
                                                <label htmlFor="upload" style={{ border: "1px dashed #D9D9D9", borderRadius: "10px", padding: "20px", background: "#F6FAFF", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", height: "60px" }}>
                                                    <input id="upload" hidden type="file" accept=".pdf" onChange={(event) => uploadHandler(event, "job_desc")} />
                                                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
                                                        <p style={{ fontStyle: "normal", fontWeight: 600, fontSize: "12px", marginTop: "0px", marginBottom: "5px", color: "#1E2228" }}>Upload</p>
                                                        <span style={{ fontWeight: 400, fontSize: "12px", color: "#506D85" }}>PDF Only . Max 2MB</span>
                                                    </div>
                                                    <span><img alt="" src="./upload.svg" style={{ marginRight: "10px" }} /></span>
                                                </label>
                                            </div>
                                        )}
                                        <FieldError field="job_desc" />
                                    </div>
                                    <div style={{ border: "2px", width: "48%" }}>
                                        <p style={{ fontWeight: "700", fontSize: "16px" }}>Miscellaneous File <span style={{ fontWeight: 400, color: T.muted }}></span></p>
                                        {miscellaneousFileName ? (
                                            <p style={{ fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "left", maxWidth: "220px" }}>
                                                {miscellaneousFileName.length > 20 ? `${miscellaneousFileName.slice(0, 20)}...` : miscellaneousFileName}
                                                <img style={{ marginLeft: "10px", cursor: "pointer" }} onClick={() => { setMiscellaneousFileName(null); setData(prev => ({ ...prev, miscellaneous_file: null })); }} width={12} height={12} src={close} alt="Remove" />
                                            </p>
                                        ) : (
                                            <div className={Styles.expectedStartDateContainer}>
                                                <label htmlFor="upload-miscellaneous-file" style={{ border: "1px dashed #D9D9D9", borderRadius: "10px", padding: "20px", background: "#F6FAFF", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", height: "60px" }}>
                                                    <input id="upload-miscellaneous-file" hidden type="file" accept=".pdf" onChange={(event) => uploadHandler(event, "miscellaneous_file")} />
                                                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
                                                        <p style={{ fontStyle: "normal", fontWeight: 600, fontSize: "12px", marginTop: "0px", marginBottom: "5px", color: "#1E2228" }}>Upload</p>
                                                        <span style={{ fontWeight: 400, fontSize: "12px", color: "#506D85" }}>PDF Only . Max 2MB</span>
                                                    </div>
                                                    <span><img alt="" src="./upload.svg" style={{ marginRight: "10px" }} /></span>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Hiring Process */}
                                <div style={{ border: "2px", width: "87%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px" }}>Hiring Process</p>
                                    <div className={Styles.expectedStartDateContainer} style={{ width: "100%" }}>
                                        <textarea value={data.company_process} onChange={(e) => setData({ ...data, company_process: e.target.value })} placeholder="Type company process" style={{ width: "100%", height: "85px" }} />
                                    </div>
                                </div>
                            </p>
                        </div>

                        {/* ── Skills ── */}
                        <div className={Styles.first_container} style={{ marginTop: "30px" }}>
                            <p className={Styles.first_container_heading} style={{ color: "#FF2C91", fontSize: "20px" }}>Skills</p>
                            <p style={{ display: "flex", marginTop: "0px", justifyContent: "space-between", flexWrap: "wrap", width: "100%", gap: "20px" }}>
                                <SkillPair skillKey="skill1" label="Skill 1 " required disabled={false}
                                    data={data} filterList={filterList} onSkillSearch={onSkillSearch} skillUpdate={skillUpdate} />
                                <SkillPair skillKey="skill2" label="Skill 2" disabled={!data.skill1}
                                    data={data} filterList={filterList} onSkillSearch={onSkillSearch} skillUpdate={skillUpdate} />
                                <SkillPair skillKey="skill3" label="Skill 3" disabled={!data.skill2}
                                    data={data} filterList={filterList} onSkillSearch={onSkillSearch} skillUpdate={skillUpdate} />
                                <SkillPair skillKey="skill4" label="Skill 4" disabled={!data.skill3}
                                    data={data} filterList={filterList} onSkillSearch={onSkillSearch} skillUpdate={skillUpdate} />
                                <SkillPair skillKey="skill5" label="Skill 5" disabled={!data.skill4}
                                    data={data} filterList={filterList} onSkillSearch={onSkillSearch} skillUpdate={skillUpdate} />

                                {/* Other Skills */}
                                <div style={{
                                    width: "32%",
                                    opacity: ['skill1', 'skill2', 'skill3', 'skill4', 'skill5'].every(k => data[k]) ? 1 : 0.4,
                                    pointerEvents: ['skill1', 'skill2', 'skill3', 'skill4', 'skill5'].every(k => data[k]) ? 'auto' : 'none',
                                    transition: "opacity 0.2s",
                                }}>
                                    <Styled.HeadingDiv style={{ fontWeight: "700", fontSize: "16px", width: "100%", marginBottom: "8px" }}>Other</Styled.HeadingDiv>
                                    <Styled.AutosearchDiv style={{ width: "100%" }}>
                                        <AutoSearchBox
                                            onSearch={(fieldName, val) => debouncedSearch(fieldName, val)}
                                            fieldName="otherSkills_all"
                                            options={otherSkillOptions}
                                            selectedValues={data.otherSkills}
                                            setSelectedValues={(loc) => otherSkillsUpdate(loc)}
                                            placeholder={['skill1', 'skill2', 'skill3', 'skill4', 'skill5'].every(k => data[k]) ? "Select skill" : "Fill all 5 skills first"}
                                        />
                                    </Styled.AutosearchDiv>
                                </div>
                            </p>
                            {errorMessage && <p style={{ color: "#f04638", fontSize: 12, marginTop: 4, marginBottom: 0, fontWeight: 500 }}>⚠ {errorMessage}</p>}
                            <FieldError field="skill1" />
                        </div>

                        {/* ── Role Specific Requirements ── */}
                        <div className={Styles.first_container} style={{ marginTop: "30px" }}>
                            <p className={Styles.first_container_heading} style={{ color: "#FF2C91", fontSize: "20px" }}>Role Specific Requirements</p>
                            <p style={{ display: "flex", marginTop: "0px", justifyContent: "space-between", flexWrap: "wrap", width: "100%", gap: "20px" }}>
                                {[1, 2, 3, 4, 5].map(n => (
                                    <div key={n} style={{ width: "48%" }}>
                                        <p style={{ fontWeight: "700", fontSize: "16px", width: "100%" }}>Question {n}</p>
                                        <div className={Styles.expectedStartDateContainer} style={{ width: "100%" }}>
                                            <textarea value={data[`question${n}`] || ""} onChange={(e) => [question1Update, question2Update, question3Update, question4Update, question5Update][n - 1](e.target.value)} placeholder="Enter question" style={{ paddingTop: "10px", width: "100%", height: "100px" }} />
                                        </div>
                                    </div>
                                ))}
                            </p>
                        </div>

                        {/* ── Other Details ── */}
                        <div className={Styles.first_container}>
                            <p className={Styles.first_container_heading} style={{ color: "#FF2C91", fontSize: "20px" }}>Other Details</p>
                            <p style={{ display: "flex", marginTop: "0px", justifyContent: "space-between", flexWrap: "wrap", width: "100%", gap: "20px" }}>

                                {/* Assigned Recruiter */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px", width: "100%", marginBottom: "8px" }}>
                                        Assigned Recruiter Name<span style={{ color: "#f04638" }}>*</span>
                                    </p>
                                    <div>
                                        <Select
                                            ref={recruiterSelectRef}
                                            mode="multiple"
                                            placeholder="Select Recruiter"
                                            optionFilterProp="children"
                                            value={data.assigned_recruiter_id}
                                            allowClear={false}
                                            style={{ width: "100%" }}
                                            dropdownStyle={{ maxHeight: "400px", overflow: "auto", padding: "8px", borderRadius: "8px", boxShadow: "0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08)" }}
                                            listHeight={256}
                                            maxTagCount={3}
                                            maxTagPlaceholder={(omitted) => `+ ${omitted.length} more`}
                                            optionLabelProp="label"
                                            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                                            menuItemSelectedIcon={null}
                                            onChange={(selectedValues) => {
                                                const selectedRecruiters = selectedValues.map((value) => {
                                                    const recruiter = recruiterOptions.find(item => item.value === value);
                                                    return { id: value, name: recruiter ? recruiter.recruiter_name : "" };
                                                });
                                                setData({ ...data, assigned_recruiter_id: selectedValues, recruiter_name: selectedRecruiters.map(r => r.name) });
                                                setError("");
                                            }}
                                        >
                                            {recruiterOptions.map(item => (
                                                <Select.Option key={item.value} value={item.value} label={item.label} style={{ padding: "8px 12px", margin: "4px 0", borderRadius: "4px", cursor: "pointer", transition: "background-color 0.3s" }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                        <Checkbox checked={(data.assigned_recruiter_id || []).includes(item.value)} style={{ pointerEvents: "none" }} />
                                                        <span>{item.label}</span>
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </div>
                                    <FieldError field="assigned_recruiter_id" />
                                </div>

                                {/* Assigned TL */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px", width: "100%" }}>Assigned TL Name<span style={{ color: "#f04638" }}>*</span></p>
                                    <div className={Styles.expectedStartDateContainer}>
                                        <Select placeholder="Select TL" optionFilterProp="children" value={data.assigned_tl_id} onChange={(e) => setData({ ...data, assigned_tl_id: e })}>
                                            {TLOptions?.map(item => <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>)}
                                        </Select>
                                    </div>
                                    <FieldError field="assigned_tl_id" />
                                </div>

                                {/* Assessment Required */}
                                <div style={{ width: "32%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px", width: "100%" }}>Assessment Required<span style={{ color: "#f04638" }}>*</span></p>
                                    <div className={Styles.expectedStartDateContainer}>
                                        <Select placeholder="Select option" optionFilterProp="children" value={data.assessment_required}
                                            onChange={(e) => setData({ ...data, assessment_required: e, assessment_link: e === "no" ? "" : data.assessment_link })}>
                                            {assessmentRequiredOptions?.map(item => <Select.Option key={item.key} value={item.key}>{item.value}</Select.Option>)}
                                        </Select>
                                    </div>
                                </div>

                                {data.assessment_required === "yes" && (
                                    <div style={{ width: "100%" }}>
                                        <p style={{ fontWeight: "700", fontSize: "16px", width: "100%" }}>Assessment Link<span style={{ color: "#f04638" }}>*</span></p>
                                        <div className={Styles.expectedStartDateContainer} style={{ width: "87%" }}>
                                            <textarea value={data.assessment_link || ""} onChange={(e) => setData({ ...data, assessment_link: e.target.value })} placeholder="Type link" style={{ paddingTop: "10px", width: "100%", height: "50px" }} />
                                        </div>
                                    </div>
                                )}

                                <div style={{ width: "100%" }}>
                                    <p style={{ fontWeight: "700", fontSize: "16px", width: "100%" }}>Remarks/Pre-requisite</p>
                                    <div className={Styles.expectedStartDateContainer} style={{ width: "100%" }}>
                                        <textarea value={data.remarks} onChange={(e) => setData({ ...data, remarks: e.target.value })} placeholder="Remarks..." style={{ paddingTop: "10px", width: "100%", height: "157px" }} />
                                    </div>
                                </div>
                            </p>

                            {/* Bottom error summary */}
                            {Object.keys(fieldErrors).length > 0 && (
                                <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
                                    <p style={{ color: "#f04638", fontWeight: 700, fontSize: 13, margin: "0 0 6px" }}>Please fix the following before submitting:</p>
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        {Object.values(fieldErrors).map((msg, i) => (
                                            <li key={i} style={{ color: "#f04638", fontSize: 13, marginBottom: 2 }}>{msg}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                                <Styled.SearchButton onClick={handleSubmitClick}>
                                    {isUpdateMode ? "Update Demand" : isNewDemandMode ? "Create New Demand" : "Submit"}
                                </Styled.SearchButton>
                            </div>
                        </div>
                    </div>
                </Styled.ColumnDiv>
            </Styled.MainDiv>
        </>
    );
};

export default DemandForm;